import React, { Profiler, ProfilerOnRenderCallback, useContext, useEffect, useRef, useState } from 'react'
import useTheme from '@mui/system/useTheme'
import { useSetAtom } from 'jotai'
import { IssueEntity, IssueType } from 'types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { CompanyEntity } from 'types/CompanyEntity'
import { BarSeriesType, CurveType, LineSeriesType, YAxis } from '@mui/x-charts/models'
import { useLoaderData, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip'
import Grid from '@mui/material/Grid'
import { seriesDropdownItems, TradesGraphToolbar } from './Toolbar'
import { TradesGraphTooltip } from './Tooltip'
import { mapTimeSelectorKeyToDate, TIME_SELECTOR_ROUTE_PARAM, TradesGraphTimeSelector } from './TimeSelector'
import { InstrumentApi, TradesType } from '../../../../api/InstrumentApi'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'
import { TradeRecord } from 'types/Instrument'
import { useQuery } from '../../../../hooks/useQuery'
import { formatBigMoney, formatNumber } from '../../../../packages/utils/numberFormatters'
import InfoBlock from '../../../components/InfoBlock'
import { TimeLineChart } from '../../../../components/TimeLineChart'
import { ChartCompanyEvents } from './CompanyEvents'
import { MouseZoom } from './MouseZoom'
import { IssueSubscriptionPeriod } from './IssueSubscriptionPeriod'
import { UserMeContext } from '../../../../contexts/UserMeContext'
import { CompanyTabs, selectedTabAtom } from './CompanyTabs'

let totalDuration = 0
let timer: number

const onRender: ProfilerOnRenderCallback = (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
    totalDuration += actualDuration
    return
    console.log(id, phase, { actualDuration, baseDuration, totalDuration, startTime, commitTime })

    clearTimeout(timer)
    timer = setTimeout(() => {
        totalDuration = 0
        console.log('Clear total duration')
    }, 1500)
}

const instrumentApi = new InstrumentApi()
const issueApi = new IssueApi()
const warrantApi = new WarrantApi()

const ISSUE_TYPES: IssueType[] = ['Rights issue', 'ABB Directed issue', 'Directed issue', 'Non-cash issue', 'IPO']
export const SELECTED_ISSUE_ROUTE_PARAM = 'issueId'

export const TradesGraph = ({
    companyId,
    instrumentId,
    instrumentName,
}: {
    companyId: string
    instrumentId: string
    instrumentName: string | null
}) => {
    const company = useLoaderData() as CompanyEntity
    const [trades, setTrades] = useState<TradesType | null>(null)
    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [warrants, setWarrants] = useState<WarrantEntity[] | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { user } = useContext(UserMeContext)
    const userSettings = user?.settings?.tradesGraph
    const [curveType, setCurveType] = useState<CurveType>(userSettings?.curveType ?? 'monotoneX')
    const tabsContainerRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()
    const query = useQuery()
    const { i18n } = useTranslation()
    const navigate = useNavigate()
    const setSelectedTab = useSetAtom(selectedTabAtom)

    const defaultSerie = userSettings?.serie ?? 'marketcap'
    const graphRoutingParam = 'serie'
    const selectedSerie = seriesDropdownItems.find(({ id }) => id === query.get(graphRoutingParam))?.id || defaultSerie

    useEffect(() => {
        setTrades(null)
        setIsLoading(true)
        void (async () => {
            try {
                const result = await Promise.allSettled([
                    (async () => {
                        const trades = instrumentApi.getTrades(instrumentId)

                        for await (const t of trades) {
                            setTrades(t)
                        }
                    })(),
                    issueApi
                        .findByCompanyId(companyId)
                        .then((issues) => setIssues(issues.filter(({ type }) => ISSUE_TYPES.includes(type)))),
                    warrantApi.findByCompanyId(companyId).then(setWarrants),
                ])

                result.filter((r) => r.status === 'rejected').forEach((e) => console.error(e))
            } catch (err) {
                console.error(err)
            } finally {
                setIsLoading(false)
            }
        })()
    }, [companyId, instrumentId])

    const handleSelectIssue = (type: 'warrant' | 'issue', id: string) => {
        query.set(SELECTED_ISSUE_ROUTE_PARAM, `${type}__${id}`)
        setSelectedTab('selected-event')
        navigate(`?${query.toString()}`, { replace: true })

        if (tabsContainerRef.current) {
            const boundingRect = tabsContainerRef.current.getBoundingClientRect()

            if (boundingRect.top + boundingRect.height / 2 > window.innerHeight + window.scrollY) {
                window.scrollTo({ top: boundingRect.top - window.innerHeight / 2 })
            }
        }
    }
    const handleChangeCurveType = (curveType: CurveType) => setCurveType(curveType)

    const selectedAlternative = mapTimeSelectorKeyToDate(query.get(TIME_SELECTOR_ROUTE_PARAM) || '')
    const yAxisValueFormatter: YAxis<'band'>['valueFormatter'] = React.useMemo(
        () => (value: number) => {
            if (value === null) return ''

            if (selectedSerie === 'marketcap') {
                return formatBigMoney(value, {
                    locale: i18n.language,
                    decimals: 0,
                })
            }

            const maxDecimals = value > 1 ? 2 : value < 0.01 ? 3 : 4

            return formatNumber(value, { minDecimals: 0, maxDecimals, locale: i18n.language })
        },
        [i18n.language, selectedSerie],
    )

    const { series, xAxisData } = React.useMemo(() => {
        if (!trades) return { series: [], xAxisData: [] }

        const filterByDate = (t: Pick<TradeRecord, 'recordDate'>) =>
            new Date(t.recordDate).getTime() >= selectedAlternative.start &&
            new Date(t.recordDate).getTime() <= selectedAlternative.end

        const xAxisData = (trades?.share.map((d) => new Date(d.recordDate).getTime()) || []).filter(
            (d) => d >= selectedAlternative.start && d <= selectedAlternative.end,
        )

        return {
            xAxisData,
            series: [
                {
                    data: trades.share.filter(filterByDate).map((t) => (t[selectedSerie] ? +t[selectedSerie] : null)),
                    type: 'line',
                    id: 'trades',
                    label: instrumentName || 'Aktie',
                    color: theme.palette.info.main,
                    area: true,
                    connectNulls: true,
                    curve: curveType,
                },
                ...Object.keys(trades.unit).map(
                    (key) =>
                        ({
                            data: trades.unit[key]
                                .filter(filterByDate)
                                .map((t) => (t[selectedSerie] ? +t[selectedSerie] : null)),
                            type: 'line',
                            id: `units-${key}`,
                            color: theme.palette.FE.main,
                            label: 'BTA/BTU',
                            connectNulls: true,
                            curve: curveType,
                        }) as LineSeriesType,
                ),
                ...Object.keys(trades.right).map(
                    (key) =>
                        ({
                            data: trades.right[key]
                                .filter(filterByDate)
                                .map((t) => (t[selectedSerie] ? +t[selectedSerie] : null)),
                            type: 'line',
                            id: `rights-${key}`,
                            color: theme.palette.FE[200],
                            label: 'Rätter',
                            connectNulls: true,
                            curve: curveType,
                        }) as LineSeriesType,
                ),
            ] as (LineSeriesType | BarSeriesType)[],
        }
    }, [
        trades,
        instrumentName,
        theme.palette.info.main,
        theme.palette.FE,
        curveType,
        selectedAlternative.start,
        selectedAlternative.end,
        selectedSerie,
    ])

    const issuesForSelectedInstrument = issues?.filter((i) => i.instrumentId === instrumentId) ?? null
    const warrantsForSelectedInstrument = warrants?.filter((w) => w.shareInstrumentId === instrumentId) ?? null

    if (!isLoading && !trades?.share.length) return null

    return (
        <Profiler id='trades-graph' onRender={onRender}>
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <InfoBlock noPadding>
                        <TimeLineChart
                            height={340}
                            hideTooltip
                            isLoading={isLoading}
                            isSlim
                            series={series}
                            surfaceChildren={issues?.map((i) => (
                                <IssueSubscriptionPeriod
                                    key={i.id}
                                    issueId={i.id}
                                    onClick={() => handleSelectIssue('issue', i.id)}
                                />
                            ))}
                            skipAnimation
                            xAxisData={xAxisData}
                            yAxisValueFormatter={yAxisValueFormatter}
                        >
                            <ChartCompanyEvents
                                onSelect={(type, id) => handleSelectIssue(type, id)}
                                issues={issuesForSelectedInstrument}
                                warrants={warrantsForSelectedInstrument}
                            />
                            <TradesGraphToolbar
                                defaultSerie={defaultSerie}
                                selectedCurveType={curveType}
                                onChangeCurveType={handleChangeCurveType}
                            />
                            <ChartsTooltipContainer trigger='axis'>
                                <TradesGraphTooltip currency={company.currency} trades={trades} />
                            </ChartsTooltipContainer>
                            <MouseZoom />
                        </TimeLineChart>

                        <TradesGraphTimeSelector isLoading={isLoading} shareTrades={trades?.share ?? null} />
                    </InfoBlock>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                    <div ref={tabsContainerRef} style={{ height: '100%' }}>
                        <CompanyTabs />
                    </div>
                </Grid>
            </Grid>
        </Profiler>
    )
}
